import React, { useState } from 'react';
import { 
    genreList, 
    moodList, 
    tempoRanges, 
    durationRanges, 
} from '../../config/constants';
import { 
    durationToDurationRange,
    bpmToTempoRange
} from '../../utils/utils';
import {
    MenuItem,
    Select,
    InputLabel,
    Checkbox,
    ListItemText,
    Box,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { styled } from "@mui/system";
import './search-filter.css';
import { 
    CustomTextField, 
    CustomFormControl
} from '../form-elements/form-elements';

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
    textAlign: 'left',
    margin: '2px',
    padding: '2px 6px',
    "& .MuiCheckbox-root": {
        padding: '2px',
    },
    "& .MuiSvgIcon-root": {
        borderRadius: '3px',
        height: '20px',
    },
}));


const initialSearchFilter = {
    title: '',
    genre: [],
    mood: [],
    duration: [],
    tempo: []
}


const SearchFilter = ({data, onFilterChange}) => {
    const [searchFilter, setSearchFilter] = useState(initialSearchFilter);

    const handleFilterChange = (field, value) => {
        const updatedFilter = { ...searchFilter, [field]: value };
        setSearchFilter(updatedFilter); 
        applyFilter(updatedFilter); 
    }

    

    const applyFilter = (filters) => {
        const filteredData = data.filter((track) => {
            const matchesName = track.title.toLowerCase().includes(filters.title.toLowerCase());
            
            const matchesMoods = filters.mood.length
              ? filters.mood.some((mood) => 
                    track.mood_1.includes(mood) ||
                    track.mood_2.includes(mood) ||
                    track.mood_3.includes(mood) ||
                    track.mood_4.includes(mood)
                )
              : true;
            
            const matchesGenres = filters.genre.length
              ? filters.genre.some((genre) => track.genre.includes(genre))
              : true;
            
            const matchesDurations = filters.duration.length
              ? filters.duration.some((duration) => durationToDurationRange(track.duration) === duration)
              : true;

            const matchesTempos = filters.tempo.length
              ? filters.tempo.some((tempo) =>  bpmToTempoRange(track.bpm) === tempo)
              : true;
      
            return matchesName && matchesMoods && matchesGenres && matchesDurations && matchesTempos;
        });
        onFilterChange(filteredData)
    }

    const handleClearFilters = () => {
        setSearchFilter(initialSearchFilter); 
        applyFilter(initialSearchFilter); 
    }

    const IsFilterActive = () => {
        const checks = [
            searchFilter.title.length > 0 ? true : false,
            searchFilter.genre.length > 0 ? true : false,
            searchFilter.mood.length > 0 ? true : false,
            searchFilter.tempo.length > 0 ? true : false,
            searchFilter.duration.length > 0 ? true : false
        ];
        return checks.some(Boolean);
    }


    return (
        <div className='search-filter-outer-container'>
            <div className='search-filter-container'>
                <Box sx={{ display:'flex', flexDirection: 'row' }}>
                    <Box>
                        <CustomTextField
                            size="small"
                            label="SEARCH FOR A SONG..."
                            name="title"
                            value={searchFilter.title}
                            onChange={(e) => handleFilterChange("title", e.target.value)}
                            variant="outlined"
                            fullWidth
                            className='text-search-input'
                        />
                    </Box>

                    {/* genre */}
                    <CustomFormControl 
                        fullWidth 
                        variant="outlined" 
                        size="small"
                        sx={{
                            display: {
                                xs:false,
                                sm:true,
                                md:true
                            }
                        }}
                        className="search-form-control"
                    >
                        <InputLabel 
                            id='genre'
                        >
                            GENRES
                        </InputLabel>
                        <Select
                            multiple
                            value={searchFilter.genre}
                            onChange={(e) => {handleFilterChange("genre", e.target.value)}}
                            labelId='genre'
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <em>Select an option</em>
                                }
                                return selected.join(', ');
                                }
                            }
                        >
                            <CustomMenuItem disabled value="">
                                <em>Select an option</em>
                            </CustomMenuItem>
                            {genreList.map((genre) => (
                                <CustomMenuItem key={genre} value={genre}>
                                    <Checkbox checked={searchFilter.genre.includes(genre)} />
                                    <ListItemText primary={genre} />
                                </CustomMenuItem>
                            ))}
                        </Select>
                    </CustomFormControl>

                    {/* moods */}
                    <CustomFormControl 
                        fullWidth 
                        variant="outlined" 
                        size="small"
                        sx={{
                            display: {
                                xs:'none',
                                sm:'flex',
                                md:'flex',
                                lg:'flex'
                            }
                        }}
                        className="search-form-control"
                    >
                        <InputLabel
                            id='mood'
                            sx={{
                                top: '16px', 
                                left: '10px', 
                                fontSize: '14px', 
                            }}
                        >
                            MOODS
                        </InputLabel>
                        <Select
                            multiple
                            value={searchFilter.mood}
                            onChange={(e) => handleFilterChange("mood", e.target.value)
                            }
                            labelId='mood'
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {moodList.map((mood) => (
                                <CustomMenuItem key={mood} value={mood}>
                                    <Checkbox checked={searchFilter.mood.includes(mood)} />
                                    <ListItemText primary={mood} />
                                </CustomMenuItem>
                            ))}
                        </Select>
                    </CustomFormControl>

                    {/* duration */}
                    <CustomFormControl 
                        fullWidth 
                        variant="outlined" 
                        size="small"
                        sx={{
                            display: {
                                xs:'none',
                                sm:'none',
                                md:'flex',
                                lg:'flex'
                            }
                        }}
                        className="search-form-control"
                    >
                        <InputLabel
                            id='duration'
                            sx={{
                                top: '16px', 
                                left: '10px', 
                                fontSize: '14px', 
                            }}
                        >
                            DURATIONS
                        </InputLabel>
                        <Select
                            multiple
                            value={searchFilter.duration}
                            onChange={(e) =>
                                handleFilterChange("duration", e.target.value)
                            }
                            labelId='duration'
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {durationRanges.map((duration) => (
                                <CustomMenuItem key={duration} value={duration}>
                                    <Checkbox checked={searchFilter.duration.includes(duration)} />
                                    <ListItemText primary={duration} />
                                </CustomMenuItem>
                            ))}
                        </Select>
                    </CustomFormControl>

                    {/* bpm */}
                    <CustomFormControl 
                        fullWidth 
                        variant="outlined" 
                        size="small"
                        sx={{
                            display: {
                                xs:'none',
                                sm:'none',
                                md:'none',
                                lg: 'flex'
                            }
                        }}
                        className="search-form-control"
                    >
                        <InputLabel
                            id='tempo'
                            sx={{
                                top: '16px', 
                                left: '10px', 
                                fontSize: '14px', 
                            }}
                        >
                            TEMPOS
                        </InputLabel>
                        <Select
                            multiple
                            value={searchFilter.tempo}
                            onChange={(e) =>
                                handleFilterChange("tempo", e.target.value)
                            }
                            labelId='tempo'
                            renderValue={(selected) => selected.join(', ')}
                        >
                            {tempoRanges.map((tempo) => (
                                <CustomMenuItem key={tempo} value={tempo}>
                                    <Checkbox checked={searchFilter.tempo.includes(tempo)} />
                                    <ListItemText primary={tempo} />
                                </CustomMenuItem>
                            ))}
                        </Select>
                    </CustomFormControl>
                </Box>
            </div>
            {/* clear filters button */}
            {IsFilterActive() &&
                <div className='clear-filters-container'
                    onClick={() => handleClearFilters()}
                >
                    <CancelOutlinedIcon 
                        fontSize='small'
                        sx={{
                            marginRight: '3px'
                        }}
                    />
                    <div>Clear Filters</div>
                </div>
            }
        </div>
    )
}

export default SearchFilter;